import BaseTableNew from 'components/atoms/base-table/new';
import { useEffect, type FC } from 'react';
import BaseButton from 'components/atoms/baseButton';
import {
  useNewlyTableDataQueryFilter,
  useConsoleTable,
  useGetNewlyTableData,
  CCM_CONSOLE_PAGE_SIZE,
} from 'infrastructure/hooks/ccm/console';
import { useTranslation } from 'react-i18next';
import {
  checkIsPageAvailable,
  getIsPodFiltered,
} from 'infrastructure/hooks/ccm/console/utils';
import { UserAddOutlined } from '@ant-design/icons';

import CcmConsoleTableBlock from '../table-block';
import { EmptyPodFilterState } from '../empty-pod-filter-state';
import { useColumns } from './columns';
import s from './styles.module.scss';
import { NumberOfCallSelectBox } from '../select-boxes/number-of-call';

import type {
  INewlyTableData,
  INewlyTableDataFilters,
} from 'infrastructure/interfaces';

interface NewlyEnrolledTableProps {}

const NewlyEnrolledTable: FC<NewlyEnrolledTableProps> = () => {
  const { filter, updateFilter } = useNewlyTableDataQueryFilter();
  const { data, isLoading, isError } = useGetNewlyTableData(filter);
  const columns = useColumns({ filter });
  const { t } = useTranslation();
  const items = filter['n-allExcluded'] ? [] : data?.data || [];
  const total = filter['n-allExcluded'] ? 0 : data?.count || 0;
  const isPodFiltered = getIsPodFiltered(
    filter.podFilter,
    !!filter['n-allExcluded'],
  );

  const { onSort, pushToQueue, rowSelection, disableAddToQueue, rowClassName } =
    useConsoleTable<INewlyTableDataFilters, INewlyTableData>({
      filter,
      updateFilter,
      tablePrefix: 'n-',
      excludeSortFields: [
        'n-page',
        'n-allExcluded',
        'n-clinicGuid',
        'n-excludeClinicGuid',
        'podFilter',
        'patientGuid',
      ],
      tableName: 'newly',
    });

  useEffect(() => {
    const isPageExist = checkIsPageAvailable(
      filter['n-page'],
      total,
      items.length,
    );
    if (!isPageExist && !isLoading && filter['n-page'] !== 1) {
      updateFilter({ 'n-page': undefined });
    }
  }, [filter['n-page'], total, isLoading, items.length]);

  return (
    <CcmConsoleTableBlock
      title={t('labels.newlyEnrolledPatients')}
      total={total}
      headerActions={
        <div className={s.filters}>
          <div className={s['filters-item']}>
            <NumberOfCallSelectBox
              tablePrefix="n-"
              cyPrefix="follow-up"
              value={filter['n-numberOfCalls']}
              updateFilter={updateFilter}
            />
          </div>
          <BaseButton
            label={t('labels.addToWorkQueue')}
            className={s['add-queue-btn']}
            icon={<UserAddOutlined />}
            disabled={disableAddToQueue}
            onClick={pushToQueue}
          />
        </div>
      }
    >
      {items.length === 0 && isPodFiltered && !isLoading && !isError ? (
        <EmptyPodFilterState />
      ) : (
        <BaseTableNew
          items={items}
          columns={columns}
          bordered
          loading={isLoading}
          pagination={{
            current: filter['n-page'],
            pageSize: CCM_CONSOLE_PAGE_SIZE,
            total,
            position: ['bottomRight'],
          }}
          onChangePage={(newPage) => {
            updateFilter({ 'n-page': newPage });
          }}
          onSort={onSort}
          dataCy="newly-enrolled-patients-table"
          tableRowSelection={rowSelection}
          className={s.table}
          rowClassName={rowClassName}
          rowHoverable
          changeOddRowColor={false}
        />
      )}
    </CcmConsoleTableBlock>
  );
};

export default NewlyEnrolledTable;
